import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, Zap, CheckCircle, Eye, EyeOff } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Las contraseñas no coinciden'); return; }
    if (password.length < 6) { setError('Minimo 6 caracteres'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-center">
          <p style={{ color: 'var(--text-muted)' }}>Token no proporcionado.</p>
          <Link to="/login" className="text-sm mt-2 inline-block" style={{ color: 'var(--accent-primary)' }}>Ir al login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent-primary)' }}>
              <Zap size={22} className="text-white" />
            </div>
            <span className="text-xl font-heading font-bold" style={{ color: 'var(--text-primary)' }}>SalesAI</span>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Nueva contraseña</p>
        </div>

        <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
          {done ? (
            <div className="text-center py-4">
              <CheckCircle size={40} className="mx-auto mb-3 text-green-400" />
              <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Contraseña restablecida</h3>
              <Link to="/login" className="inline-block mt-2 text-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
                Iniciar sesion
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 rounded-lg text-sm bg-red-500/10 text-red-400 border border-red-500/20">{error}</div>
              )}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Nueva contraseña</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    data-testid="reset-password-input"
                    className="w-full px-4 py-2.5 pr-10 rounded-lg border text-sm outline-none"
                    style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Confirmar contraseña</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  data-testid="reset-confirm-input"
                  className="w-full px-4 py-2.5 rounded-lg border text-sm outline-none"
                  style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full py-2.5 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--accent-primary)' }}
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                Restablecer contraseña
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
